/**
 * Content Templates Dashboard
 *
 * AI-generated visual marketing templates for GMB, social media, websites, and ads.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Button,
  Icon,
  Badge,
  Image,
  Skeleton,
  SkeletonText,
  useColorModeValue,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Tooltip,
  Flex,
  Progress,
  Divider,
  Select,
  Textarea,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useCustomToast } from '../../utils/customToast';
import {
  FaImage,
  FaGoogle,
  FaInstagram,
  FaFacebook,
  FaDesktop,
  FaUsers,
  FaBullhorn,
  FaAd,
  FaDownload,
  FaCopy,
  FaRedo,
  FaMagic,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { SectionLabel } from './DashboardWrapper';
import {
  getVerticalTemplates,
  generateTemplates,
  CONTENT_TYPE_INFO,
} from '../../services/contentTemplateService';
import { useUser } from '../../contexts/UserContext';
import { logger } from '../../utils/logger';

// Icon mapping for content types
const CONTENT_ICONS = {
  gmb_post: FaGoogle,
  website_hero: FaDesktop,
  about_page: FaUsers,
  social_instagram: FaInstagram,
  social_facebook: FaFacebook,
  paid_ad_display: FaBullhorn,
  paid_ad_social: FaAd,
};

// Fallback list of verticals if API fails
const FALLBACK_VERTICALS = [
  { vertical: 'junk_removal', display_name: 'Junk Removal', has_templates: false },
  { vertical: 'hvac', display_name: 'HVAC', has_templates: false },
  { vertical: 'hotel', display_name: 'Hotels', has_templates: false },
  { vertical: 'restaurant', display_name: 'Restaurants', has_templates: false },
  { vertical: 'automotive', display_name: 'Automotive', has_templates: false },
  { vertical: 'healthcare', display_name: 'Healthcare', has_templates: false },
  { vertical: 'retail', display_name: 'Retail', has_templates: false },
  { vertical: 'professional_services', display_name: 'Professional Services', has_templates: false },
  { vertical: 'generic_business', display_name: 'General Business', has_templates: false },
];

// Color mapping for content types
const CONTENT_COLORS = {
  gmb_post: 'blue',
  website_hero: 'purple',
  about_page: 'teal',
  social_instagram: 'pink',
  social_facebook: 'facebook',
  paid_ad_display: 'orange',
  paid_ad_social: 'red',
};

// Industry-specific content strategy data
const INDUSTRY_CONTENT_STRATEGIES = {
  junk_removal: {
    industry: 'Junk Removal',
    overview: 'In the junk removal industry, visual proof of transformation is everything. Customers want to see the dramatic before-and-after results that demonstrate your team\'s efficiency and professionalism.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Before/after transformation photos, time-lapse videos of cleanouts, team photos showing friendly professionals, and customer testimonial clips.',
        why: 'People share satisfying cleanup transformations. These posts create emotional responses and showcase the immediate value you provide.'
      },
      website: {
        title: 'Website',
        content: 'Hero images of your branded trucks and uniformed team, gallery of completed projects organized by type (residential, commercial, hoarding), and trust badges.',
        why: 'Establishes credibility instantly. Visitors decide within seconds if they trust you—professional imagery builds that trust.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Action shots of your team at work, before/after project photos, images of your fleet, and photos showing eco-friendly disposal practices.',
        why: 'GMB photos appear in local search results and Maps. Businesses with quality photos receive 42% more direction requests and 35% more website clicks.'
      }
    }
  },
  hvac: {
    industry: 'HVAC',
    overview: 'HVAC services are often emergency-driven and trust-dependent. Your content should emphasize reliability, technical expertise, and the comfort you bring to homes and businesses.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Seasonal maintenance tips, energy-saving advice, behind-the-scenes of installations, team spotlights, and weather-related service reminders.',
        why: 'Educational content positions you as the expert. When the AC breaks in July, they\'ll remember the company that taught them about filter maintenance.'
      },
      website: {
        title: 'Website',
        content: 'Professional photos of completed installations, certified technician badges, financing options graphics, and seasonal service promotion banners.',
        why: 'HVAC purchases are significant investments. Professional imagery reduces buyer anxiety and reinforces that you\'re established and trustworthy.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Photos of your service vehicles, technicians in uniform, completed installations, and your office/warehouse facility.',
        why: 'Local searches for HVAC spike during extreme weather. Updated GMB photos help you stand out when customers are making quick decisions.'
      }
    }
  },
  hotel: {
    industry: 'Hotels & Hospitality',
    overview: 'Hospitality is a visual industry where ambiance sells. Your content must transport potential guests into the experience before they ever arrive.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Stunning room and amenity photos, local attraction highlights, guest experiences, seasonal packages, and behind-the-scenes of your hospitality team.',
        why: 'Travel decisions are aspirational. Beautiful imagery triggers the emotional desire to experience what you\'re showing.'
      },
      website: {
        title: 'Website',
        content: 'High-resolution room galleries, virtual tours, amenity showcases, local area guides, and event space photography.',
        why: 'Your website is your digital lobby. The visual experience must match or exceed the in-person experience guests expect.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Exterior photos, lobby and common areas, room types, dining options, pool/fitness areas, and event spaces.',
        why: 'Travelers compare hotels directly in Google Maps. Comprehensive, professional photos increase booking inquiries significantly.'
      }
    }
  },
  restaurant: {
    industry: 'Restaurants',
    overview: 'Food is inherently visual and emotional. Your content should make mouths water and convey the unique atmosphere that makes dining with you special.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Beautifully plated dishes, chef preparation videos, seasonal menu highlights, customer celebrations, and kitchen behind-the-scenes.',
        why: 'Food content is among the most shared on social media. Craveable imagery drives both immediate visits and long-term brand awareness.'
      },
      website: {
        title: 'Website',
        content: 'Menu item photography, interior ambiance shots, private dining spaces, team/chef photos, and reservation call-to-actions.',
        why: 'Diners research restaurants online before visiting. Professional food photography can increase online orders by up to 30%.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Signature dishes, interior and patio seating, exterior storefront, drink specials, and happy customer moments.',
        why: '75% of diners check photos before choosing a restaurant. GMB photos directly influence foot traffic and reservations.'
      }
    }
  },
  automotive: {
    industry: 'Automotive',
    overview: 'Whether sales or service, automotive businesses must convey trust and expertise. Customers are making significant financial decisions and need visual reassurance.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'New inventory spotlights, maintenance tips, customer delivery photos, service bay tours, and staff introductions.',
        why: 'Car buying is emotional. Showing happy customers with their new vehicles and your team\'s expertise builds community trust.'
      },
      website: {
        title: 'Website',
        content: 'Inventory photography with multiple angles, service department images, financing graphics, and facility tours.',
        why: 'Most car buyers research extensively online. High-quality inventory photos reduce the friction to scheduling test drives.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Showroom floor, service bays, customer lounge amenities, exterior signage, and featured vehicles.',
        why: 'Local automotive searches are high-intent. Professional GMB presence increases calls and direction requests from ready buyers.'
      }
    }
  },
  healthcare: {
    industry: 'Healthcare',
    overview: 'Healthcare content must balance professionalism with approachability. Patients want to feel confident in your expertise while also feeling welcomed and cared for.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Health tips and education, staff spotlights, facility updates, community involvement, and patient success stories (with consent).',
        why: 'Healthcare social content builds familiarity and trust. When people need care, they choose providers they feel they already know.'
      },
      website: {
        title: 'Website',
        content: 'Provider headshots and bios, facility virtual tours, treatment room photos, and welcoming waiting area imagery.',
        why: 'Patients research providers online. Professional, warm imagery reduces healthcare anxiety and increases appointment bookings.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Exterior building photos, clean and modern interior spaces, friendly staff photos, and accessibility features.',
        why: 'Patients often search for providers by location. Updated GMB photos build confidence before the first visit.'
      }
    }
  },
  retail: {
    industry: 'Retail',
    overview: 'Retail is about creating desire and urgency. Your visual content should showcase products beautifully while conveying the unique shopping experience you offer.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'New arrival highlights, styling inspiration, customer features, flash sale announcements, and store event coverage.',
        why: 'Retail social media drives both immediate sales and store visits. Shoppable content shortens the path from discovery to purchase.'
      },
      website: {
        title: 'Website',
        content: 'Product photography from multiple angles, lifestyle imagery showing products in use, store interior photos, and promotional banners.',
        why: 'E-commerce conversion rates increase dramatically with high-quality product imagery. Every product needs to be visually compelling.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Storefront photos, interior shopping areas, featured products, and seasonal displays.',
        why: 'Local retail searches often lead to same-day visits. GMB photos help shoppers visualize your store before they arrive.'
      }
    }
  },
  professional_services: {
    industry: 'Professional Services',
    overview: 'Professional services sell expertise and trust. Your content should convey competence, approachability, and the premium nature of your offerings.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Industry insights and thought leadership, team accomplishments, client success stories, community involvement, and company culture.',
        why: 'B2B decisions are relationship-driven. Social content that showcases expertise and personality attracts ideal clients.'
      },
      website: {
        title: 'Website',
        content: 'Professional team headshots, modern office environment, service process visuals, and credentials/certifications display.',
        why: 'Professional service buyers vet providers thoroughly. Polished imagery signals the quality of work clients can expect.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Office exterior and interior, conference rooms, team photos, and branded elements.',
        why: 'Even B2B services get local searches. A complete GMB profile with professional photos increases credibility and inquiries.'
      }
    }
  },
  generic_business: {
    industry: 'Your Business',
    overview: 'Every business benefits from strategic visual content. The key is showcasing what makes you unique while building trust with potential customers.',
    platforms: {
      social: {
        title: 'Social Media',
        content: 'Behind-the-scenes content, team highlights, customer success stories, industry tips, and community involvement.',
        why: 'Social media humanizes your brand. People buy from businesses they know, like, and trust—consistent content builds all three.'
      },
      website: {
        title: 'Website',
        content: 'Professional hero images, team photos, service/product galleries, facility images, and trust indicators.',
        why: 'Your website is often the first impression. Quality visuals keep visitors engaged and moving toward conversion.'
      },
      gmb: {
        title: 'Google My Business',
        content: 'Exterior photos, interior spaces, team at work, products or services in action, and your brand identity.',
        why: 'Businesses with photos on GMB receive 42% more requests for directions and 35% more clicks to their websites.'
      }
    }
  }
};

// Get industry strategy with fallback
const getIndustryStrategy = (vertical) => {
  if (!vertical) return INDUSTRY_CONTENT_STRATEGIES.generic_business;
  
  // Try exact match first
  if (INDUSTRY_CONTENT_STRATEGIES[vertical]) {
    return INDUSTRY_CONTENT_STRATEGIES[vertical];
  }
  
  // Try to match partial vertical names
  const verticalLower = vertical.toLowerCase();
  for (const [key, strategy] of Object.entries(INDUSTRY_CONTENT_STRATEGIES)) {
    if (verticalLower.includes(key) || key.includes(verticalLower)) {
      return strategy;
    }
  }
  
  // Return generic with customized industry name
  return {
    ...INDUSTRY_CONTENT_STRATEGIES.generic_business,
    industry: vertical.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  };
};

/**
 * Platform Strategy Card - Modern bubble card design
 */
const PlatformStrategyCard = ({ platform, platformKey, icon: PlatformIcon, colorScheme }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const tagBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const tagColor = useColorModeValue(`${colorScheme}.600`, `${colorScheme}.200`);
  const hoverBg = useColorModeValue('gray.50', 'gray.750');

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      p={5}
      shadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.2s ease"
      _hover={{
        shadow: 'md',
        transform: 'translateY(-2px)',
        borderColor: `${colorScheme}.200`,
      }}
      h="100%"
    >
      {/* Card Header */}
      <HStack spacing={3} mb={4}>
        <Flex
          w="44px"
          h="44px"
          bgGradient={`linear(135deg, ${colorScheme}.400, ${colorScheme}.600)`}
          borderRadius="xl"
          justify="center"
          align="center"
          shadow="sm"
        >
          <Icon as={PlatformIcon} color="white" boxSize={5} />
        </Flex>
        <Text fontWeight="bold" fontSize="lg" color={headingColor}>
          {platform.title}
        </Text>
      </HStack>

      {/* What Works Section */}
      <Box mb={4}>
        <Badge
          bg={tagBg}
          color={tagColor}
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="wide"
          mb={2}
        >
          What Works
        </Badge>
        <Text fontSize="sm" color={textColor} lineHeight="1.7">
          {platform.content}
        </Text>
      </Box>

      {/* Why It Works Section */}
      <Box
        bg={hoverBg}
        borderRadius="xl"
        p={4}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          w="3px"
          bgGradient={`linear(to-b, ${colorScheme}.400, ${colorScheme}.600)`}
          borderRadius="full"
        />
        <Text fontSize="xs" fontWeight="bold" color={tagColor} mb={1} ml={2}>
          Why It Works
        </Text>
        <Text fontSize="sm" color={textColor} lineHeight="1.7" ml={2}>
          {platform.why}
        </Text>
      </Box>
    </Box>
  );
};

/**
 * Industry Content Strategy Section Component - Modern Design
 */
const IndustryContentStrategySection = ({ vertical }) => {
  const strategy = getIndustryStrategy(vertical);
  
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedText = useColorModeValue('gray.500', 'gray.500');
  
  const platformConfig = {
    social: { icon: FaInstagram, color: 'pink' },
    website: { icon: FaDesktop, color: 'purple' },
    gmb: { icon: FaGoogle, color: 'blue' }
  };

  return (
    <Box mb={8}>
      {/* Modern Header Banner */}
      <Box
        bg={cardBg}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        p={6}
        mb={5}
        position="relative"
        overflow="hidden"
      >
        {/* Subtle decorative gradient orb */}
        <Box
          position="absolute"
          top="-50px"
          right="-50px"
          w="200px"
          h="200px"
          bgGradient="radial(blue.400, transparent 70%)"
          opacity={0.1}
          borderRadius="full"
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="30%"
          w="150px"
          h="150px"
          bgGradient="radial(cyan.400, transparent 70%)"
          opacity={0.08}
          borderRadius="full"
        />
        
        <VStack align="start" spacing={4} position="relative">
          {/* Industry Badge */}
          <HStack spacing={3}>
            <Badge
              bgGradient="linear(135deg, blue.500, cyan.500)"
              color="white"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="bold"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {strategy.industry}
            </Badge>
            <Badge
              bg="transparent"
              color={mutedText}
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              borderWidth="1px"
              borderColor={borderColor}
            >
              Content Strategy
            </Badge>
          </HStack>

          {/* Main Headline */}
          <Box>
            <Text
              fontSize={{ base: '2xl', md: '3xl' }}
              fontWeight="bold"
              color={headingColor}
              lineHeight="short"
            >
              Visual Content That Converts
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* Platform Strategy Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
        {Object.entries(strategy.platforms).map(([key, platform]) => {
          const config = platformConfig[key];
          return (
            <PlatformStrategyCard
              key={key}
              platform={platform}
              platformKey={key}
              icon={config.icon}
              colorScheme={config.color}
            />
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

/**
 * Template Card Component
 */
const TemplateCard = ({ template, onClick }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useCustomToast();

  const ContentIcon = CONTENT_ICONS[template.content_type] || FaImage;
  const colorScheme = CONTENT_COLORS[template.content_type] || 'gray';
  const contentInfo = CONTENT_TYPE_INFO[template.content_type] || {};

  const handleDownload = (e) => {
    e.stopPropagation();
    window.open(template.image_url, '_blank');
  };

  const handleCopyUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(template.image_url);
    toast({
      title: 'URL copied!',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      shadow="sm"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        shadow: 'lg',
        transform: 'translateY(-4px)',
        borderColor: 'blue.400',
      }}
      onClick={() => onClick(template)}
      overflow="hidden"
    >
      <Box position="relative" overflow="hidden">
        <Image
          src={template.image_url}
          alt={template.title}
          objectFit="cover"
          h="180px"
          w="100%"
          fallback={<Skeleton h="180px" />}
        />
        <Badge
          position="absolute"
          top={2}
          left={2}
          colorScheme={colorScheme}
          variant="solid"
          fontSize="xs"
        >
          <Icon as={ContentIcon} mr={1} />
          {contentInfo.name || template.content_type}
        </Badge>
      </Box>
      <Box p={4}>
        <VStack align="start" spacing={2}>
          {template.business_name && (
            <Badge colorScheme="green" variant="subtle" fontSize="xs" borderRadius="full">
              {template.business_name}
            </Badge>
          )}
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
            {template.title}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={2}>
            {template.description}
          </Text>
          <HStack spacing={1} mt={1}>
            <Tooltip label="Download image">
              <IconButton
                icon={<FaDownload />}
                size="xs"
                variant="ghost"
                colorScheme="blue"
                onClick={handleDownload}
                aria-label="Download"
              />
            </Tooltip>
            <Tooltip label="Copy URL">
              <IconButton
                icon={<FaCopy />}
                size="xs"
                variant="ghost"
                colorScheme="green"
                onClick={handleCopyUrl}
                aria-label="Copy URL"
              />
            </Tooltip>
            <Tooltip label="Open in new tab">
              <IconButton
                icon={<FaExternalLinkAlt />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={handleDownload}
                aria-label="Open"
              />
            </Tooltip>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

/**
 * Loading skeleton for template cards
 */
const TemplateCardSkeleton = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      shadow="sm"
    >
      <Skeleton h="180px" />
      <Box p={4}>
        <SkeletonText noOfLines={3} spacing={3} />
      </Box>
    </Box>
  );
};

/**
 * Main Content Templates Dashboard Component
 */
// Content type options for selection
const CONTENT_TYPE_OPTIONS = [
  { value: 'gmb_post', label: 'Google My Business', icon: FaGoogle, color: 'blue' },
  { value: 'website_hero', label: 'Website Hero', icon: FaDesktop, color: 'purple' },
  { value: 'social_instagram', label: 'Instagram Post', icon: FaInstagram, color: 'pink' },
  { value: 'social_facebook', label: 'Facebook Post', icon: FaFacebook, color: 'facebook' },
  { value: 'paid_ad_display', label: 'Display Ad', icon: FaBullhorn, color: 'orange' },
  { value: 'paid_ad_social', label: 'Social Ad', icon: FaAd, color: 'red' },
];

const ContentTemplatesDashboard = ({ reviewsData = [], sectionLabel, demoMode, demoData }) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [availableVerticals, setAvailableVerticals] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [selectedContentType, setSelectedContentType] = useState('gmb_post'); // Default to GMB
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');

  const { vertical: userVertical } = useUser();
  const toast = useCustomToast();

  // Color mode values - consistent with other dashboards
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Get unique business names from reviewsData
  // reviewsData items have a "name" field (from MongoDB/backend)
  const businesses = useMemo(() => {
    const unique = new Set(reviewsData.map(r => r.name).filter(Boolean));
    return Array.from(unique).sort();
  }, [reviewsData]);

  // Format vertical for display (e.g., "nashville_personal_training" -> "Nashville Personal Training")
  const formatVerticalDisplay = (v) => {
    if (!v) return 'Your Business';
    return v.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Load templates on mount - only for user's vertical
  useEffect(() => {
    const loadData = async () => {
      if (demoMode && demoData) {
        setTemplates(demoData.templates || []);
        setAvailableVerticals([{ vertical: 'junk_removal', display_name: 'Junk Removal', has_templates: true }]);
        setSelectedVertical('junk_removal');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        // Use the user's actual vertical (not mapped) so the prompt includes their real vertical name
        const userVerticalKey = userVertical || 'generic_business';
        const userVerticalDisplay = formatVerticalDisplay(userVerticalKey);

        // Only show the user's vertical
        setAvailableVerticals([{
          vertical: userVerticalKey,
          display_name: userVerticalDisplay,
          has_templates: false,
        }]);
        setSelectedVertical(userVerticalKey);

        // Try to load cached templates
        try {
          const templateData = await getVerticalTemplates(userVerticalKey);
          setTemplates(templateData.templates || []);
        } catch (e) {
          // No cached templates - that's okay
          setTemplates([]);
        }
      } catch (error) {
        logger.error('Error loading content templates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userVertical, demoMode, demoData]);

  // Handle vertical change
  const handleVerticalChange = useCallback(async (newVertical) => {
    setSelectedVertical(newVertical);
    setIsLoading(true);

    try {
      const templateData = await getVerticalTemplates(newVertical);
      setTemplates(templateData.templates || []);
    } catch (e) {
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle generate
  const handleGenerate = useCallback(async (forceRegenerate = false) => {
    if (!selectedContentType) {
      toast({
        title: 'Select Content Type',
        description: 'Please select a content type to generate',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(10);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 15, 90));
      }, 2000);

      const result = await generateTemplates(selectedVertical, [selectedContentType], {
        businessName: selectedBusiness || undefined,
        customPrompt: customPrompt || undefined,
        forceRegenerate,
      });

      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (result.success) {
        // Append new templates to existing (keep all)
        setTemplates(prev => [...prev, ...(result.templates || [])]);
        toast({
          title: result.cached ? 'Template Loaded' : 'Template Generated!',
          description: result.cached
            ? 'Using cached template'
            : `Generated new ${CONTENT_TYPE_OPTIONS.find(o => o.value === selectedContentType)?.label || 'template'}`,
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Generation issue',
          description: result.error || 'Template failed to generate',
          status: 'warning',
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  }, [selectedVertical, selectedContentType, selectedBusiness, customPrompt, toast]);

  // Group templates by content type
  const templatesByType = useMemo(() => {
    const grouped = {};
    templates.forEach(template => {
      const type = template.content_type;
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(template);
    });
    return grouped;
  }, [templates]);

  // Content type tabs
  const contentTypes = Object.keys(CONTENT_TYPE_INFO);

  // Loading state
  if (isLoading) {
    return (
      <Box>
        {!demoMode && <SectionLabel description="AI-generated visual content templates">
          {sectionLabel || 'Content Templates'}
        </SectionLabel>}
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          shadow="md"
          p={6}
        >
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
            {[1, 2, 3, 4].map(i => (
              <TemplateCardSkeleton key={i} />
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {!demoMode && <SectionLabel description="AI-generated visual content templates for your marketing channels.">
        {sectionLabel || 'Content Templates'}
      </SectionLabel>}

      {/* Industry Content Strategy Section */}
      <IndustryContentStrategySection vertical={userVertical} />

      {/* Header Controls */}
      <Box
        bg={cardBg}
        borderColor={borderColor}
        borderWidth="1px"
        borderRadius="lg"
        shadow="md"
        mb={6}
        p={6}
      >
        <VStack align="stretch" spacing={4}>
          {/* Top row: Business name and cached count */}
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={3}>
              <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1} borderRadius="full">
                {availableVerticals[0]?.display_name || 'Your Business'}
              </Badge>
              {templates.length > 0 && (
                <Badge colorScheme="green" variant="subtle" fontSize="xs" px={2} py={1} borderRadius="full">
                  {templates.length} templates cached
                </Badge>
              )}
            </HStack>
          </Flex>

          {/* Business Name Selector */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium">Business Name</FormLabel>
            <Select
              placeholder={businesses.length > 0 ? "Select a business" : "No businesses available"}
              value={selectedBusiness}
              onChange={(e) => setSelectedBusiness(e.target.value)}
              isDisabled={isGenerating || businesses.length === 0}
              size="md"
            >
              {businesses.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            {businesses.length === 0 && (
              <Text fontSize="xs" color="orange.500" mt={1}>
                No businesses found in your data. Load some review data first.
              </Text>
            )}
          </FormControl>

          {/* Content Type Selection - Pill Button Style */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium">Content Type</FormLabel>
            <Flex flexWrap="wrap" gap={3}>
              {CONTENT_TYPE_OPTIONS.map(option => {
                const isSelected = selectedContentType === option.value;
                const IconComponent = option.icon;
                return (
                  <Button
                    key={option.value}
                    size="md"
                    variant={isSelected ? "solid" : "outline"}
                    colorScheme={isSelected ? option.color : "gray"}
                    borderRadius="full"
                    px={4}
                    py={2}
                    onClick={() => setSelectedContentType(option.value)}
                    isDisabled={isGenerating}
                    leftIcon={<Icon as={IconComponent} boxSize={4} />}
                    fontWeight="medium"
                    shadow={isSelected ? "md" : "sm"}
                    _hover={{
                      transform: 'translateY(-2px)',
                      shadow: 'md',
                    }}
                    transition="all 0.2s"
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Flex>
          </FormControl>

          {/* Custom Prompt Input */}
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium">Custom Prompt (optional)</FormLabel>
            <Textarea
              placeholder="Add details like: 'sunny day, red truck, smiling team members'"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              isDisabled={isGenerating}
              size="sm"
              rows={2}
              maxLength={500}
            />
          </FormControl>

          {/* Generate Button */}
          <HStack spacing={4}>
            <Button
              leftIcon={<FaMagic />}
              colorScheme="blue"
              onClick={() => handleGenerate(false)}
              isLoading={isGenerating}
              loadingText="Generating..."
              isDisabled={!selectedContentType}
            >
              Generate Template
            </Button>

            {templates.length > 0 && (
              <Tooltip label="Force regenerate (overwrites cache)">
                <IconButton
                  icon={<FaRedo />}
                  colorScheme="orange"
                  variant="outline"
                  onClick={() => handleGenerate(true)}
                  isDisabled={isGenerating || !selectedContentType}
                  aria-label="Regenerate"
                />
              </Tooltip>
            )}
          </HStack>
        </VStack>
      </Box>

      {/* Generation Progress */}
      {isGenerating && (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          shadow="sm"
          p={4}
          mb={6}
        >
          <Text mb={2} fontSize="sm" fontWeight="medium">
            Generating templates...
          </Text>
          <Progress
            value={generationProgress}
            size="sm"
            colorScheme="blue"
            borderRadius="full"
            hasStripe
            isAnimated
          />
        </Box>
      )}

      {/* Empty State */}
      {!templates.length && !isGenerating && (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          shadow="sm"
          p={6}
          mb={6}
          textAlign="center"
        >
          <Icon as={FaImage} boxSize={12} color="gray.400" mb={4} />
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            No Templates Yet
          </Text>
          <Text fontSize="sm" color="gray.500" maxW="md" mx="auto">
            Select a business and content type above, then click "Generate" to create AI-powered marketing visuals.
            Each template costs ~$0.002 and is cached for 7 days.
          </Text>
        </Box>
      )}

      {/* Template Tabs */}
      {templates.length > 0 && (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          shadow="md"
          overflow="hidden"
        >
          <Tabs
            variant="soft-rounded"
            colorScheme="blue"
            index={activeTab}
            onChange={setActiveTab}
          >
            <TabList flexWrap="wrap" gap={2} p={4} bg={statBg}>
              <Tab fontSize="sm" fontWeight="medium">
                <Icon as={FaImage} mr={2} />
                All ({templates.length})
              </Tab>
              {contentTypes.map(type => {
                const typeTemplates = templatesByType[type] || [];
                if (typeTemplates.length === 0) return null;
                const TypeIcon = CONTENT_ICONS[type] || FaImage;
                const info = CONTENT_TYPE_INFO[type] || {};
                return (
                  <Tab key={type} fontSize="sm" fontWeight="medium">
                    <Icon as={TypeIcon} mr={2} />
                    {(info.name || type).split(' ')[0]} ({typeTemplates.length})
                  </Tab>
                );
              })}
            </TabList>

            <TabPanels>
              {/* All Templates */}
              <TabPanel p={6}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                  {templates.map(template => (
                    <TemplateCard
                      key={template.template_id}
                      template={template}
                      onClick={setSelectedTemplate}
                    />
                  ))}
                </SimpleGrid>
              </TabPanel>

              {/* Type-specific tabs */}
              {contentTypes.map(type => {
                const typeTemplates = templatesByType[type] || [];
                if (typeTemplates.length === 0) return null;
                return (
                  <TabPanel key={type} p={6}>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                      {typeTemplates.map(template => (
                        <TemplateCard
                          key={template.template_id}
                          template={template}
                          onClick={setSelectedTemplate}
                        />
                      ))}
                    </SimpleGrid>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </Box>
      )}

      {/* Template Preview Modal */}
      <Modal
        isOpen={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        size="xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent bg={cardBg} borderRadius="xl" shadow="2xl">
          <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
            {selectedTemplate?.title}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {selectedTemplate && (
              <VStack spacing={5}>
                <Box
                  borderRadius="lg"
                  overflow="hidden"
                  borderWidth="1px"
                  borderColor={borderColor}
                  w="100%"
                >
                  <Image
                    src={selectedTemplate.image_url}
                    alt={selectedTemplate.title}
                    maxH="400px"
                    w="100%"
                    objectFit="contain"
                    bg={statBg}
                  />
                </Box>

                {selectedTemplate.business_name && (
                  <Box w="100%">
                    <Text fontWeight="semibold" fontSize="sm" mb={2} color="gray.500">
                      Business
                    </Text>
                    <Badge colorScheme="green" variant="subtle" fontSize="sm" borderRadius="full" px={3} py={1}>
                      {selectedTemplate.business_name}
                    </Badge>
                  </Box>
                )}

                <Box w="100%">
                  <Text fontWeight="semibold" fontSize="sm" mb={2} color="gray.500">
                    Description
                  </Text>
                  <Text fontSize="sm">
                    {selectedTemplate.description}
                  </Text>
                </Box>

                {selectedTemplate.prompt_used && (
                  <Box p={4} bg={statBg} borderRadius="lg" w="100%">
                    <Text fontWeight="semibold" fontSize="sm" mb={2} color="blue.500">
                      Prompt Used
                    </Text>
                    <Text fontSize="sm">{selectedTemplate.prompt_used}</Text>
                  </Box>
                )}

                {selectedTemplate.suggested_caption && (
                  <Box p={4} bg={statBg} borderRadius="lg" w="100%">
                    <Text fontWeight="semibold" fontSize="sm" mb={2} color="gray.500">
                      Suggested Caption
                    </Text>
                    <Text fontSize="sm">{selectedTemplate.suggested_caption}</Text>
                  </Box>
                )}

                <Divider />

                <HStack spacing={3} w="100%" justify="center">
                  <Button
                    leftIcon={<FaDownload />}
                    colorScheme="blue"
                    onClick={() => window.open(selectedTemplate.image_url, '_blank')}
                  >
                    Download
                  </Button>
                  <Button
                    leftIcon={<FaCopy />}
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedTemplate.image_url);
                      toast({
                        title: 'URL copied!',
                        status: 'success',
                        duration: 2000,
                      });
                    }}
                  >
                    Copy URL
                  </Button>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ContentTemplatesDashboard;
